import React, { useEffect } from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CartDrawer from '../components/cart/CartDrawer';
import ProductCard from '../components/products/ProductCard';
import { Link } from 'react-router-dom';
import { products, categories, siteImages, steps, performanceBenefits, features, CONTACT_INFO } from '../data/mock';
import { ArrowRight, Truck, ShieldCheck, RotateCcw, Package, Wrench, Gauge, CheckCircle2, Hammer, Star } from 'lucide-react';

const featureIcons = [Wrench, Truck, RotateCcw, ShieldCheck];
const benefitIcons = [Hammer, Gauge, Package, CheckCircle2];

const HomePage = () => {
  const featuredProduct = products.find(p => p.slug === 'compact-cordless-drill-set-with-accessories') || products[0];
  const gridSlugs = ['multifunction-auto-pipe-wrench','180-in-1-precision-electric-screwdriver-set','advanced-waterproof-voltage-tester','professional-cordless-angle-grinder','precision-digital-clamp-meter-kit','laser-range-finder','jigsaw','rugged-waterproof-tool-storage-case'];
  const productsGrid = gridSlugs.map(s => products.find(p => p.slug === s)).filter(Boolean);
  const bestsellers = productsGrid.length ? productsGrid : products.slice(0, 8);

  useEffect(() => { document.title = 'Ironworks Tools | Quality Hardware & Power Tools'; }, []);

  const stats = [
    { label: 'Tools in stock', value: '14+' },
    { label: 'Free USA shipping', value: '$0' },
    { label: 'Return window', value: '30 days' },
    { label: 'USPS delivery', value: '2–4 days' },
  ];

  return (
    <div className="min-h-screen bg-background text-dark-700">
      <Header />
      <CartDrawer />
      <main>
        {/* Hero — calm split */}
        <section className="bg-gradient-to-b from-dark-50 to-background">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20 grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
            <div className="lg:col-span-6">
              <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-athletic-100 text-athletic-800 text-xs font-semibold tracking-wide rounded-full mb-6"><Star className="w-3.5 h-3.5" /> Trusted tools, honest prices</span>
              <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-semibold text-dark-900 leading-[1.05]">The right tools, calmly chosen for every job</h1>
              <p className="mt-6 text-lg text-dark-600 max-w-xl leading-relaxed">Ironworks Tools brings together measuring gear, hand tools, cordless power tools and workshop storage &mdash; dependable equipment shipped free across the USA.</p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/shop" className="inline-flex items-center gap-2 rounded-xl bg-athletic-600 px-7 py-4 font-semibold text-white transition hover:bg-athletic-700 shadow-button">Shop all tools <ArrowRight className="w-4 h-4" /></Link>
                <Link to="/about" className="inline-flex items-center gap-2 rounded-xl border border-dark-300 bg-white px-7 py-4 font-semibold text-dark-800 hover:border-athletic-500 hover:text-athletic-700 transition">Our story</Link>
              </div>
              <div className="mt-8 flex items-center gap-6 text-sm text-dark-500">
                <span className="flex items-center gap-2"><Truck className="w-4 h-4 text-athletic-600" /> Free USPS shipping</span>
                <span className="flex items-center gap-2"><RotateCcw className="w-4 h-4 text-athletic-600" /> 30-day returns</span>
              </div>
            </div>
            <div className="lg:col-span-6 relative">
              <div className="relative rounded-3xl overflow-hidden shadow-elevated">
                <img src={siteImages.heroMain} alt="Neatly arranged hand tools on a workbench" className="w-full h-[320px] lg:h-[460px] object-cover" />
              </div>
              <div className="absolute -bottom-6 -left-4 sm:-left-6 hidden sm:flex items-center gap-3 bg-white rounded-2xl shadow-elevated px-5 py-4 border border-dark-100">
                <div className="w-11 h-11 rounded-xl bg-athletic-600 text-white flex items-center justify-center"><Hammer className="w-5 h-5" /></div>
                <div><p className="font-display text-base font-semibold text-dark-900 leading-none">Built to last</p><p className="text-xs text-dark-500 mt-1">Chosen for real work</p></div>
              </div>
            </div>
          </div>
          {/* Stat strip */}
          <div className="border-t border-dark-200">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-2 lg:grid-cols-4 divide-x divide-dark-200">
              {stats.map((s) => (
                <div key={s.label} className="py-6 px-4 text-center lg:text-left">
                  <p className="font-display text-3xl font-semibold text-athletic-700 leading-none">{s.value}</p>
                  <p className="text-xs uppercase tracking-widest text-dark-400 mt-2">{s.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Categories — calm image-top cards */}
        <section className="bg-background py-16 sm:py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-end justify-between mb-10">
              <div><span className="inline-block text-xs font-semibold uppercase tracking-[0.2em] text-athletic-600 mb-2">Browse by category</span><h2 className="font-display text-3xl sm:text-4xl font-semibold text-dark-900">Find what your project needs</h2></div>
              <Link to="/shop" className="hidden sm:inline-flex items-center gap-1.5 text-sm font-semibold text-athletic-700 hover:gap-2.5 transition-all">All tools <ArrowRight className="w-4 h-4" /></Link>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-5">
              {categories.map((category) => (
                <Link key={category.id} to={`/shop?category=${category.slug}`} className="group bg-white rounded-2xl overflow-hidden border border-dark-200 hover:border-athletic-300 hover:shadow-card transition-all">
                  <div className="aspect-[4/3] overflow-hidden bg-dark-100">
                    <img src={category.image} alt={category.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                  <div className="p-4">
                    <h3 className="font-display text-base font-semibold text-dark-900 leading-tight group-hover:text-athletic-700 transition-colors">{category.name}</h3>
                    <p className="text-xs text-dark-500 mt-1 font-medium">{category.productCount} item{category.productCount > 1 ? 's' : ''} &rarr;</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Bestsellers */}
        <section className="bg-white py-16 sm:py-20 border-y border-dark-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-end justify-between mb-10">
              <div><span className="inline-block text-xs font-semibold uppercase tracking-[0.2em] text-athletic-600 mb-2">Popular picks</span><h2 className="font-display text-3xl sm:text-4xl font-semibold text-dark-900">Shop favorites</h2></div>
              <Link to="/shop" className="hidden sm:inline-flex items-center gap-1.5 text-sm font-semibold text-athletic-700 hover:gap-2.5 transition-all">View all <ArrowRight className="w-4 h-4" /></Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">{bestsellers.map((p) => (<ProductCard key={p.id} product={p} />))}</div>
          </div>
        </section>

        {/* Flagship spotlight */}
        <section className="bg-background py-16 sm:py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
            <div className="rounded-3xl bg-white border border-dark-200 p-8 shadow-card"><img src={featuredProduct.image} alt={featuredProduct.name} className="w-full max-w-md mx-auto object-contain" /></div>
            <div>
              <span className="inline-block text-xs font-semibold uppercase tracking-[0.2em] text-athletic-600 mb-3">Featured this week</span>
              <h2 className="font-display text-3xl sm:text-4xl font-semibold text-dark-900 mb-4">{featuredProduct.name}</h2>
              <p className="text-dark-600 mb-5 max-w-lg leading-relaxed">{featuredProduct.description.slice(0, 200)}&hellip;</p>
              <p className="text-3xl font-semibold text-athletic-700 mb-7">${featuredProduct.price.toFixed(2)}</p>
              <div className="flex flex-wrap gap-3">
                <Link to={`/product/${featuredProduct.slug}`} className="inline-flex items-center gap-2 px-7 py-4 bg-athletic-600 text-white rounded-xl font-semibold hover:bg-athletic-700 transition shadow-button">View product</Link>
                <Link to="/shop" className="inline-flex items-center gap-2 px-7 py-4 border border-dark-300 bg-white text-dark-800 rounded-xl font-semibold hover:border-athletic-500 hover:text-athletic-700 transition">Browse more</Link>
              </div>
            </div>
          </div>
        </section>

        {/* Benefits band (calm green) */}
        <section className="bg-athletic-700 py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
              {performanceBenefits.map((b, i) => {
                const Icon = benefitIcons[i % benefitIcons.length];
                return (
                  <div key={b.id} className="text-center sm:text-left">
                    <div className="w-12 h-12 rounded-xl bg-white/15 flex items-center justify-center text-white mb-4 mx-auto sm:mx-0"><Icon className="w-6 h-6" /></div>
                    <h3 className="font-display text-xl font-semibold text-white">{b.title}</h3>
                    <p className="text-white/80 text-sm mt-1.5 leading-relaxed">{b.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* How it works */}
        <section className="bg-white py-16 sm:py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12"><span className="inline-block text-xs font-semibold uppercase tracking-[0.2em] text-athletic-600 mb-2">How it works</span><h2 className="font-display text-3xl sm:text-4xl font-semibold text-dark-900">From cart to job in four steps</h2></div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {steps.map((step) => (
                <div key={step.id} className="bg-dark-50 border border-dark-200 rounded-2xl p-6"><span className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-athletic-600 text-white font-display font-semibold text-lg">{step.id}</span><h3 className="font-display text-xl font-semibold text-dark-900 mt-4">{step.title}</h3><p className="text-dark-500 text-sm mt-2 leading-relaxed">{step.description}</p></div>
              ))}
            </div>
          </div>
        </section>

        {/* Features strip */}
        <section className="bg-dark-50 border-y border-dark-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((f, i) => {
              const Icon = featureIcons[i % featureIcons.length];
              return (
                <div key={f.id} className="flex items-start gap-3">
                  <div className="w-11 h-11 rounded-xl bg-white text-athletic-700 border border-dark-200 flex items-center justify-center flex-shrink-0"><Icon className="w-5 h-5" /></div>
                  <div><p className="font-semibold text-dark-900 text-sm">{f.title}</p><p className="text-dark-500 text-xs mt-0.5 leading-relaxed">{f.description}</p></div>
                </div>
              );
            })}
          </div>
        </section>

        {/* FAQ */}
        <section className="bg-background py-16 sm:py-20">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="font-display text-3xl sm:text-4xl font-semibold text-dark-900 text-center mb-10">Common questions</h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {[
                { q: 'How fast will my order arrive?', a: `Orders ship via USPS within 1 business day and usually land in 2 to 4 business days. Need a status update? Call us at ${CONTACT_INFO.phone}.` },
                { q: 'Is shipping really free?', a: 'Every US order ships free via USPS Standard Shipping — no minimum spend, no surprise fees at checkout.' },
                { q: 'What if a tool is not right for me?', a: 'You have 30 days from delivery to start a return. Get in touch and our team will walk you through it.' },
                { q: 'How do I reach a person?', a: `Our support team is on the line at ${CONTACT_INFO.phone} or by email at ${CONTACT_INFO.email}.` },
              ].map((faq, i) => (<div key={i} className="bg-white rounded-2xl p-6 border border-dark-200"><h3 className="font-display text-lg font-semibold text-dark-900">{faq.q}</h3><p className="mt-2 text-dark-600 text-sm leading-relaxed">{faq.a}</p></div>))}
            </div>
            <div className="text-center mt-10"><Link to="/faq" className="inline-flex items-center gap-2 text-athletic-700 hover:text-athletic-800 font-semibold">See all FAQs <ArrowRight className="w-4 h-4" /></Link></div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default HomePage;
